import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import { useScrollListener } from '../hooks';
import { messagesSelector } from '../selectors/messages';
import { getMessageLoad } from '../selectors/messageLoad';
import { RootState, RootDispatch } from '../store';
import { Message } from '../types';

import MessageSubTypes from '../models/enums/MessageSubTypes';
import MessageItem from './MessageItem/MessageItem';
import MembersList from './MembersList';

import styles from './ConversationPane.module.css';

export function ConversationPane({
  markConversationAsRead,
  messages,
  getMessages,
  messageLoad,
  timelineRef,
}: {
  markConversationAsRead: (_payload: boolean) => void;
  messages: Message[];
  getMessages: (m: string) => void;
  messageLoad: { initial: unknown; replay: unknown; direction: unknown; loading: unknown };
  timelineRef: React.RefObject<HTMLDivElement>;
}) {
  function renderMessage(message: Message, idx: number) {
    const { id, body, createdAt } = message;
    const prevMessage = messages[idx - 1];
    const monthDiff = prevMessage && createdAt.getMonth() !== prevMessage.createdAt.getMonth();
    const dayDiff = prevMessage && createdAt.getDate() !== prevMessage.createdAt.getDate();
    const today = new Date();
    const isToday =
      today.getMonth() === createdAt.getMonth() && today.getDate() === createdAt.getDate();
    let dateFormat;

    if (message.subType === MessageSubTypes.VWR_CALL) {
      return null;
    }

    if (isToday) {
      dateFormat = 'TODAY';
    } else {
      dateFormat = createdAt.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
      });
    }

    const messageElement = (
      <MessageItem
        key={message.id}
        // messageId and lastModified are returned from messageSelector,
        // but are they public props on this component or provided by connect?
        // https://tigertext.atlassian.net/browse/TMG-837
        // @ts-expect-error
        messageId={message.id}
        lastModified={message.$lastModified}
        timelineRef={timelineRef}
      />
    );

    const dateBang = (
      <div key={message.id}>
        <div className={styles.bangContainer} aria-label="date-bang">
          <div className={styles.bangPill}>{dateFormat}</div>
        </div>
        {messageElement}
      </div>
    );

    const textBang = (
      <div key={message.id}>
        <div className={styles.bangContainer} aria-label="text-bang">
          <div className={styles.bangText}>{body}</div>
        </div>
      </div>
    );

    if (id.includes('bang')) {
      if (message?.subType === MessageSubTypes.CHATBOT_MESSAGE) {
        return (
          <MessageItem
            key={message.id}
            // messageId and lastModified are returned from messageSelector,
            // but are they public props on this component or provided by connect?
            // https://tigertext.atlassian.net/browse/TMG-837
            // @ts-expect-error
            messageId={message.id}
            lastModified={message.$lastModified}
            timelineRef={timelineRef}
          />
        );
      }
      return textBang;
    }
    if (idx === 0 || (idx !== 0 && (monthDiff || dayDiff))) {
      return dateBang;
    }

    return messageElement;
  }

  const [atBottom, setAtBottom] = useState(false);
  function handleScroll(element: HTMLDivElement) {
    // element is not an Event type, so it has no property target.
    // But element itself does have the scrollTop etc. properties.
    // Does this even work?
    // https://tigertext.atlassian.net/browse/TMG-850
    // @ts-expect-error
    const { scrollTop, scrollHeight, clientHeight } = element.target;
    if (scrollHeight - scrollTop === clientHeight) {
      getMessages('BOTTOM');
      setAtBottom(true);
      return;
    }
    setAtBottom(false);
    if (scrollTop === 0 && messageLoad.initial) {
      getMessages('TOP');
    }
  }

  const [previousMessageCount, setpreviousMessageCount] = useState(0);
  useEffect(() => {
    if (messages.length > previousMessageCount && atBottom) {
      timelineRef.current && timelineRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    setpreviousMessageCount(messages.length);
  }, [atBottom, previousMessageCount, messages, timelineRef]);

  const messageAreaRef = useRef<HTMLDivElement>(null);
  useScrollListener(messageAreaRef, handleScroll);

  useEffect(() => {
    if (messageLoad.initial && !messageLoad.replay) {
      //eslint-disable-next-line scanjs-rules/call_setTimeout
      setTimeout(() => {
        timelineRef.current && timelineRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, [timelineRef, messageLoad.initial, messageLoad.replay]);

  return (
    <div
      className={styles.conversationPaneContainer}
      onClick={() => {
        markConversationAsRead(true);
      }}
      ref={messageAreaRef}
    >
      <MembersList />
      <div className={styles.messageArea}>
        {messages.map((m, i) => renderMessage(m, i))}
        <div ref={timelineRef} style={{ paddingTop: '5px' }} />
      </div>
      {messageLoad.loading && (
        <div
          className={`${styles.loader} ${
            messageLoad.direction === 'TOP' ? styles.loaderTop : styles.loaderBottom
          }`}
        >
          <FormattedMessage id={`loading`} />
        </div>
      )}
    </div>
  );
}

const mapState = (state: RootState) => {
  return {
    messages: messagesSelector(state),
    messageLoad: getMessageLoad(state),
  };
};

const mapDispatch = ({
  conversation: { markConversationAsRead },
  messages: { getMessages },
}: RootDispatch) => ({
  markConversationAsRead,
  getMessages,
});

// https://tigertext.atlassian.net/browse/TMG-837
// @ts-expect-error
export default connect(mapState, mapDispatch)(ConversationPane);
